import React, { useEffect, useRef } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useSubscription } from "@apollo/client";
import { AdminUser } from "../../api/fetchAdminUsers";
import {
  ON_REMOTE_SYNC_LOG_SUBSCRIPTION,
  RemoteSyncLogSubscriptionData,
} from "../../../graphql/subscriptions/onRemoteSyncLog";

interface SyncLogsModalProps {
  user: AdminUser | null;
  employeeId: number | null;
  isOpen: boolean;
  onClose: () => void;
}

export const SyncLogsModal: React.FC<SyncLogsModalProps> = ({
  user,
  employeeId,
  isOpen,
  onClose,
}) => {
  const logRef = useRef<HTMLPreElement>(null);

  const { data: subscriptionData } = useSubscription<RemoteSyncLogSubscriptionData>(
    ON_REMOTE_SYNC_LOG_SUBSCRIPTION,
    {
      variables: { employeeId },
      skip: !isOpen || !employeeId,
    }
  );

  const subscriptionLog = subscriptionData?.remote_sync_log?.[0];

  const syncLog = subscriptionLog
    ? {
        id: subscriptionLog.id,
        status: subscriptionLog.status,
        log: subscriptionLog.log,
        syncResults: subscriptionLog.sync_results,
        startedAt: subscriptionLog.started_at,
        completedAt: subscriptionLog.completed_at,
      }
    : user?.remoteSyncLog;

  const hasSyncData = syncLog && (syncLog.log || syncLog.syncResults);
  const isInProgress = syncLog?.status === "in_progress";

  useEffect(() => {
    if (logRef.current && isInProgress) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [syncLog?.log, isInProgress]);

  if (!user) return null;

  return (
    <Dialog className="relative z-50" onClose={onClose} open={isOpen}>
      <DialogBackdrop className="fixed inset-0 bg-black/30 backdrop-blur-sm transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200" />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="relative w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white dark:bg-gray-900 shadow-2xl transition-all data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200">
            {/* Close Button */}
            <button
              aria-label="Close dialog"
              className="absolute right-4 top-4 z-10 group"
              onClick={onClose}
            >
              <div className="relative flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 transition-all duration-300 hover:bg-gray-200 dark:hover:bg-gray-700 hover:scale-110 hover:rotate-90 active:scale-95">
                <XMarkIcon className="h-5 w-5 text-gray-600 dark:text-gray-400 transition-all duration-300 group-hover:scale-110" />
              </div>
            </button>

            {/* Header */}
            <div className="border-b border-gray-200 dark:border-gray-800 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-900 px-6 py-5">
              <DialogTitle className="text-lg font-semibold text-gray-900 dark:text-white">
                Remote Sync Logs - {user.firstName} {user.lastName}
              </DialogTitle>
              {user.companyName && (
                <p className="text-sm text-gray-500 mt-1">
                  Company: {user.companyName}
                </p>
              )}
              {syncLog && (
                <div className="mt-2 flex items-center gap-4 text-sm">
                  <span className="text-gray-500 dark:text-gray-400 flex items-center gap-2">
                    Status:{" "}
                    <span
                      className={`font-semibold flex items-center gap-1 ${
                        syncLog.status === "success"
                          ? "text-green-600"
                          : syncLog.status === "failed"
                            ? "text-red-600"
                            : "text-yellow-600"
                      }`}
                    >
                      {isInProgress && (
                        <svg
                          className="animate-spin h-4 w-4"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                      )}
                      {syncLog.status}
                    </span>
                  </span>
                  {syncLog.completedAt && (
                    <span className="text-gray-500 dark:text-gray-400">
                      Last synced: {new Date(syncLog.completedAt.endsWith("Z") ? syncLog.completedAt : syncLog.completedAt + "Z").toLocaleString()}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Content */}
            <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
              {!hasSyncData ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  No sync data available for this user.
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Sync Log */}
                  {syncLog.log && (
                    <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-4">
                      <h3 className="font-semibold text-lg mb-2 text-gray-900 dark:text-white flex items-center gap-2">
                        Sync Log
                        {isInProgress && (
                          <span className="text-xs font-normal text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 px-2 py-0.5 rounded-full">
                            Live
                          </span>
                        )}
                      </h3>
                      <pre
                        ref={logRef}
                        className="bg-gray-100 dark:bg-gray-800 p-4 rounded text-xs overflow-x-auto text-gray-900 dark:text-gray-100 whitespace-pre-wrap max-h-64 overflow-y-auto"
                      >
                        {syncLog.log}
                      </pre>
                    </div>
                  )}

                  {/* Sync Results JSON */}
                  {syncLog.syncResults && (
                    <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-4">
                      <h3 className="font-semibold text-lg mb-2 text-gray-900 dark:text-white">
                        API Requests & Responses
                      </h3>
                      <pre className="bg-gray-100 dark:bg-gray-800 p-4 rounded text-xs overflow-x-auto text-gray-900 dark:text-gray-100">
                        {JSON.stringify(syncLog.syncResults, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 px-6 py-4">
              <button
                className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-700 hover:scale-[1.02] active:scale-[0.98]"
                onClick={onClose}
                type="button"
              >
                Close
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};
